import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { useAuthStore } from "@/stores/authStore";
import { fetchState } from "@/services/cwaccService";
import { grantAccessToTenant } from "@/services/tenantManagementService";
import { parseJwt } from "@/utils";
import { mapTokenToTenants } from "@/composables/mapTokenToTenants";
import { redirectIfVersionMismatch } from "@/composables/useVersionRedirect";
import type { AppState, MobiusTenant } from "@/types";
import type { User } from "oidc-client-ts";
import type { MobiusPlanType } from "@core/types";

export type StoreState = "pending" | "loading" | "switching" | "ready" | "error";

export const useUserStore = defineStore("user", () => {
  const authUser = ref<User | null>(null);
  const tenants = ref<MobiusTenant[]>([]);
  const currentTenant = ref<MobiusTenant | null>(null);
  const storeState = ref<StoreState>("pending");
  const isNavigating = ref(false);
  // Optional override for the global LoadingScreen during specific long-running
  const loadingOverride = ref<{ message?: string; longLoad?: boolean } | null>(null);
  const appState = ref<AppState>({
    appStateLoading: true,
    showIframe: false,
    isConnectwiseConfigured: false,
    accountingConfigurations: [],
  });

  const userProfile = computed(() => authUser.value?.profile);

  const hasAdminAccess = computed(() => {
    const userGroups = (userProfile.value?.groups as string[]) || [];
    return userGroups.includes("/admin");
  });

  const needsTenantSelection = computed(() => {
    if (storeState.value === "switching" || currentTenant.value !== null) return false;
    return tenants.value.length > 1;
  });
  
  const isSetupIncomplete = computed(() => {
    if (appState.value.appStateLoading) return false;
    if (!currentTenant.value) return false;
    return (
      !appState.value.isConnectwiseConfigured ||
      !appState.value.accountingConfigurations.length
    );
  });
  
  function getPlanName(planId: string): MobiusPlanType {
    if (!planId) return "";
    if (planId.includes("LITE")) return "lite";
    if (planId.includes("STANDARD")) return "standard";
    if (planId.includes("FREE")) return "free";
    if (planId.includes("qbo")) return "qbo";
    return "";
  }

  async function initUserData(): Promise<void> {
    if (storeState.value === "ready") return;
    if (storeState.value === "loading") {
      return new Promise<void>((resolve) => {
        const checkInterval = setInterval(() => {
          if (storeState.value === "ready" || storeState.value === "error") {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);
      });
    }
    
    storeState.value = "loading";
    
    try {
      const authStore = useAuthStore();
      const kcUser = await waitForValidToken(authStore);
      
      authUser.value = kcUser;
      updateTenantsFromToken();

      storeState.value = "ready";
    } catch (err) {
      console.error("Error initializing user data:", err);
      storeState.value = "error";
    }
  }

  function updateTenantsFromToken(): void {
    let claims: Record<string, unknown> | undefined = userProfile.value;
    if (!claims?.tenant_id && authUser.value?.access_token) {
      claims = parseJwt(authUser.value.access_token);
    }
    tenants.value = mapTokenToTenants(claims);
  }

  async function waitForValidToken(authStore: ReturnType<typeof useAuthStore>): Promise<User> {
    let user: User | null = await authStore.authClient.getUser();

    const isTokenValid = (u: User | null): u is User => {
      if (!u?.access_token) return false;
      const now = Math.floor(Date.now() / 1000);
      return !u.expires_at || u.expires_at > now;
    };

    if (isTokenValid(user)) return user;

    return new Promise<User>((resolve) => {
      const intervalId = setInterval(async () => {
        user = await authStore.authClient.getUser();
        if (isTokenValid(user)) {
          clearInterval(intervalId);
          resolve(user);
        }
      }, 1000);
    });
  }

  async function refreshToken(): Promise<void> {
    const authStore = useAuthStore();
    try {
      const refreshedUser = await authStore.authClient.signinSilent();
      if (refreshedUser) {
        authUser.value = refreshedUser;
        document.cookie = `KC_ACCESS_TOKEN=${refreshedUser.access_token};path=/`;
        updateTenantsFromToken();
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
    }
  }

  async function pollForTenantGroup(tenantId: number, maxAttempts = 10, delayMs = 500): Promise<boolean> {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await refreshToken();
      const userGroups = (userProfile.value?.groups as string[]) || [];
      if (userGroups.includes(`/tenant-${tenantId}`)) {
        return true;
      }
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
    console.warn(`Polling timed out waiting for tenant-${tenantId} group membership`);
    return false;
  }

  function setCurrentTenant(tenant: MobiusTenant, targetPath?: string): boolean {
    if (redirectIfVersionMismatch(tenant, targetPath)) return true;

    storeState.value = "switching";
    currentTenant.value = tenant;
    initAppState().finally(() => storeState.value = "ready");
    return false;
  }

  async function loginToTenant(
    tenantId: number,
    tenantData: { name: string; versionTag: string; planId: string; isActive: boolean }
  ): Promise<"success" | "redirected" | "failed"> {
    storeState.value = "switching";
    loadingOverride.value = { message: "Switching to tenant...", longLoad: true };

    try {
      await grantAccessToTenant(tenantId);
      
      const success = await pollForTenantGroup(tenantId);
      
      if (success) {
        // Pass "/" so a version redirect lands on the user dashboard, not the admin page the caller happens to be on right now.
        const tenant: MobiusTenant = {
          id: tenantId,
          name: tenantData.name,
          version_tag: tenantData.versionTag,
          subscription: {
            plan_id: tenantData.planId,
            is_active: tenantData.isActive
          }
        };
        const isRedirected = setCurrentTenant(tenant, "/");
        return isRedirected ? "redirected" : "success";
      }
      
      return "failed";
    } catch (error) {
      console.error("Error logging into tenant:", error);
      throw error;
    } finally {
      storeState.value = "ready";
      loadingOverride.value = null;
    }
  }

  async function initAppState(): Promise<void> {
    if (!currentTenant.value) return;
    appState.value.appStateLoading = true;
    try {
      const data = await fetchState<AppState>(currentTenant.value.id);

      appState.value.accountingConfigurations = data?.accountingConfigurations ?? [];
      appState.value.isConnectwiseConfigured = data?.isConfiguredConnectwiseConfiguration ?? false;
      appState.value.showIframe = true;
    } catch (err) {
      const apiErr = err as { message?: string; response?: { status: number; data: { detail: string } } };
      let msg = apiErr.message;
      if (apiErr.response && [401, 403].includes(apiErr.response.status)) {
        msg = apiErr.response.data.detail;
      }
      console.error(msg);
    } finally {
      appState.value.appStateLoading = false;
    }
  }

  function logUserOut(redirectUri?: string): void {
    storeState.value = "switching"; // Prevent TenantPickerView flash during logout
    currentTenant.value = null;
    const authStore = useAuthStore();
    authStore.authLogout(redirectUri);
  }

  return {
    authUser,
    userProfile,
    tenants,
    currentTenant,
    storeState,
    isNavigating,
    loadingOverride,
    appState,
    hasAdminAccess,
    needsTenantSelection,
    isSetupIncomplete,
    initUserData,
    refreshToken,
    setCurrentTenant,
    loginToTenant,
    getPlanName,
    initAppState,
    logUserOut
  };
});
